import React, { useState } from 'react';
import playByPointLogo from '../../assets/integrations/playbypoint.webp';
import { API_URL, getAuthToken, getVenueQuery } from './internalIntegrationUtils';

type Step = 'form' | 'saving' | 'success' | 'error';

export interface PlayByPointAutoApprovalIntegrationRecord {
  id: string;
  external_id: string;
  config?: {
    domain?: string;
    facilityId?: string;
    adminEmail?: string;
    adminPassword?: string;
    autoApprovalEnabled?: boolean;
    lastExecutionAt?: string | null;
    successCount?: number;
    failureCount?: number;
  };
  last_sync_at?: string | null;
}

function formatTimeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

async function updatePlayByPointAutoApprovalIntegration(
  integrationId: string,
  config: PlayByPointAutoApprovalIntegrationRecord['config']
) {
  const token = getAuthToken();
  const res = await fetch(`${API_URL}/api/integrations/${integrationId}${getVenueQuery()}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ config }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Failed to update integration: ${res.status}`);
  }

  return res.json();
}

export function PlayByPointAutoApprovalConnectedCard({
  integration,
  onDisconnect,
  onRefresh,
}: {
  integration: PlayByPointAutoApprovalIntegrationRecord;
  onDisconnect: () => void;
  onRefresh: () => void;
}) {
  const [autoApprovalEnabled, setAutoApprovalEnabled] = useState(
    integration.config?.autoApprovalEnabled !== false
  );
  const [savingToggle, setSavingToggle] = useState(false);
  const [error, setError] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);

  const adminEmail = integration.config?.adminEmail || 'Unknown';
  const domain = integration.config?.domain || integration.external_id;
  const facilityId = integration.config?.facilityId || integration.external_id;
  const lastExecutionAt = integration.config?.lastExecutionAt || integration.last_sync_at || null;

  const handleToggle = async () => {
    const nextValue = !autoApprovalEnabled;
    setSavingToggle(true);
    setError('');

    try {
      await updatePlayByPointAutoApprovalIntegration(integration.id, {
        ...(integration.config || {}),
        autoApprovalEnabled: nextValue,
      });
      setAutoApprovalEnabled(nextValue);
      onRefresh();
    } catch (err: any) {
      setError(err.message || 'Failed to update auto-approval setting.');
    } finally {
      setSavingToggle(false);
    }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null); setError('');
    try {
      const res = await fetch(`${API_URL}/api/integrations/${integration.id}/test-playbypoint-auto-approval${getVenueQuery()}`, { method: 'POST', headers: { Authorization: `Bearer ${getAuthToken()}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Test failed: ${res.status}`);
      setTestResult(data);
    } catch (err: any) { setError(err.message || 'Test failed.'); } finally { setTesting(false); }
  };

  return (
    <div className="integration-card integration-card--connected">
      <div className="integration-card-header">
        <div className="integration-logo integration-logo--image" aria-hidden="true">
          <img className="integration-logo__img" src={playByPointLogo} alt="PlayByPoint logo" />
        </div>
        <div className="integration-title">
          <h3>PlayByPoint Auto-Approval</h3>
          <div className="integration-subtitle-row">
            <span className="integration-category">Booking Automation</span>
            <span className="integration-capability-pill">Auto-approve + create reservation</span>
          </div>
        </div>
        <div className="integration-card-header__right">
          <span className="integration-state-pill integration-state-pill--connected">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Connected
          </span>
        </div>
      </div>

      <div className="integration-meta">
        <div className="integration-meta__item">
          <div className="integration-meta__label">Domain</div>
          <div className="integration-meta__value integration-meta__value--mono">{domain}</div>
        </div>
        <div className="integration-meta__item">
          <div className="integration-meta__label">Facility ID</div>
          <div className="integration-meta__value integration-meta__value--mono">{facilityId}</div>
        </div>
      </div>

      <div className="integration-meta" style={{ marginTop: 0 }}>
        <div className="integration-meta__item">
          <div className="integration-meta__label">Provisioned Account</div>
          <div className="integration-meta__value integration-meta__value--mono">{adminEmail}</div>
        </div>
        <div className="integration-meta__item">
          <div className="integration-meta__label">Last Execution</div>
          <div className="integration-meta__value">{formatTimeAgo(lastExecutionAt)}</div>
        </div>
      </div>

      {typeof integration.config?.successCount === 'number' || typeof integration.config?.failureCount === 'number' ? (
        <div className="integration-meta" style={{ marginTop: 0 }}>
          <div className="integration-meta__item">
            <div className="integration-meta__label">Successes</div>
            <div className="integration-meta__value">{integration.config?.successCount ?? 0}</div>
          </div>
          <div className="integration-meta__item">
            <div className="integration-meta__label">Failures</div>
            <div className="integration-meta__value">{integration.config?.failureCount ?? 0}</div>
          </div>
        </div>
      ) : null}

      {error && (
        <div className="integration-error-banner" role="alert">
          {error}
        </div>
      )}
      {testResult && <div className="integration-test-steps">{testResult.steps?.map((step: any) => <div key={step.name}>{step.status === 'pass' ? '✓' : '✕'} {step.name}: {step.message}</div>)}</div>}

      <div className="integration-admin-controls">
        <div className="integration-admin-controls__row">
          <label className="integration-admin-controls__checkbox">
            <input
              type="checkbox"
              checked={autoApprovalEnabled}
              onChange={handleToggle}
              disabled={savingToggle}
            />
            Auto-Approval
          </label>
          <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
            {savingToggle ? 'Saving...' : autoApprovalEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>

      <div className="integration-footer integration-footer--connected">
        <button className="btn btn-secondary" onClick={handleTest} disabled={testing}>{testing ? 'Testing...' : 'Test connection'}</button>
        <button className="btn btn-secondary integration-disconnect-btn" onClick={onDisconnect}>
          Disconnect
        </button>
      </div>
    </div>
  );
}

export function PlayByPointAutoApprovalAvailableCard({ onSetup }: { onSetup: () => void }) {
  return (
    <div className="integration-card integration-card--available">
      <div className="integration-card-header">
        <div className="integration-logo integration-logo--image" aria-hidden="true">
          <img className="integration-logo__img" src={playByPointLogo} alt="PlayByPoint logo" />
        </div>
        <div className="integration-title">
          <h3>PlayByPoint Auto-Approval</h3>
          <div className="integration-subtitle-row">
            <span className="integration-category">Booking Automation</span>
            <span className="integration-capability-pill">Auto-approve + create reservation</span>
          </div>
        </div>
        <span className="integration-badge available">Available</span>
      </div>

      <p className="integration-description">
        Automatically log into PlayByPoint, create the reservation there, and approve the booking without manual admin intervention.
      </p>

      <div className="integration-support-note">
        Currently supported: <strong>provisioned admin credentials</strong> and <strong>auto-approval enable/disable controls</strong>.
      </div>

      <div className="integration-footer">
        <button className="btn btn-primary" onClick={onSetup}>
          Connect
        </button>
      </div>
    </div>
  );
}

export default function PlayByPointAutoApprovalIntegration({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<Step>('form');
  const [domain, setDomain] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [error, setError] = useState('');

  const token = getAuthToken();

  const handleSave = async () => {
    if (!domain.trim() || !facilityId.trim() || !adminEmail.trim() || !adminPassword.trim()) {
      setError('All fields are required.');
      return;
    }

    setError('');
    setStep('saving');

    try {
      const normalizedDomain = domain.trim().replace(/^https?:\/\//i, '').replace(/\/+$/, '');
      const res = await fetch(`${API_URL}/api/integrations${getVenueQuery()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          integrationType: 'playbypoint_auto_approval',
          externalId: facilityId.trim(),
          config: {
            domain: normalizedDomain,
            facilityId: facilityId.trim(),
            adminEmail: adminEmail.trim(),
            adminPassword: adminPassword.trim(),
            autoApprovalEnabled: true,
            scrapeMethod: 'puppeteer',
            courtMappings: {},
          },
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Failed to save: ${res.status}`);
      }

      setStep('success');
    } catch (err: any) {
      setError(err.message || 'Failed to save integration.');
      setStep('error');
    }
  };

  if (step === 'success') {
    return (
      <div className="integration-setup">
        <div className="integration-setup__header">
          <button className="integration-setup__back" onClick={onClose}>← Back</button>
          <div className="integration-setup__title">
            <div className="integration-setup__logo" style={{ background: '#0f766e' }}>P</div>
            <div>
              <h1>PlayByPoint Auto-Approval</h1>
              <p>Booking automation connected</p>
            </div>
          </div>
        </div>
        <div className="integration-setup__success">
          <div className="integration-setup__success-icon">✓</div>
          <h2>Auto-Approval Connected</h2>
          <p>Provisioned account: <strong>{adminEmail}</strong></p>
          <p>Bookings for this venue can now be auto-created in PlayByPoint and approved without manual admin work.</p>
          <div className="integration-step__actions">
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="integration-setup">
      <div className="integration-setup__header">
        <button className="integration-setup__back" onClick={onClose}>← Back</button>
        <div className="integration-setup__title">
          <div className="integration-setup__logo" style={{ background: '#0f766e' }}>P</div>
          <div>
            <h1>PlayByPoint Auto-Approval</h1>
            <p>Create PlayByPoint reservations and approve bookings automatically</p>
          </div>
        </div>
        <span className="integration-badge beta" style={{ marginLeft: 8 }}>Internal</span>
      </div>

      <div className="integration-step">
        <div className="integration-step__card">
          <h2>Connect PlayByPoint Admin Access</h2>
          <p>
            Save the PlayByPoint domain, facility ID, and admin credentials for this venue. The automation worker
            will use this account to create reservations and approve pending bookings.
          </p>

          <div className="integration-setup__field">
            <label>Domain</label>
            <input
              type="text"
              className="form-input"
              placeholder="book.topseedpickleball.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            />
          </div>

          <div className="integration-setup__field">
            <label>Facility ID</label>
            <input
              type="text"
              className="form-input"
              placeholder="1048"
              value={facilityId}
              onChange={(e) => setFacilityId(e.target.value)}
            />
          </div>

          <div className="integration-setup__field">
            <label>Admin Email</label>
            <input
              type="email"
              className="form-input"
              placeholder="luke@picklecourts.rent"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
            />
          </div>

          <div className="integration-setup__field">
            <label>Admin Password</label>
            <input
              type="password"
              className="form-input"
              placeholder="••••••••"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
            />
            <span className="integration-setup__field-hint">
              Stored in integration config for browser automation. Login validation is a later step.
            </span>
          </div>

          {error && <div className="integration-setup__error">{error}</div>}

          <div className="integration-step__actions">
            <button className="btn btn-primary" onClick={handleSave} disabled={step === 'saving'}>
              {step === 'saving' ? 'Connecting...' : 'Connect Auto-Approval'}
            </button>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
